/*
 * Groply theme.js (foundation). Progressive enhancement only: every
 * feature below is optional and the store works without JavaScript.
 *
 * Data-attribute API (see _work/CONTRACT.md):
 *   details[data-drawer]          mobile drawers (aria-expanded, Esc, scroll lock, [data-drawer-close],
 *                                 page outside the open drawer made inert, Tab kept inside)
 *   details[data-dropdown]        desktop disclosures (Esc, click outside, one open at a time)
 *   [data-header]                 gets .is-scrolled; sets --header-height on :root
 *   [data-qty] + [data-qty-step]  quantity steppers around an <input type="number">
 *   [data-autosubmit]             submit the parent form on change (a select changed from the
 *                                 keyboard submits on Enter or blur instead)
 *   [data-gallery]                media gallery with [data-gallery-item] + [data-gallery-thumb]
 *   .rte table                    wrapped in .table-wrap for horizontal scroll
 *   [data-cart-count] / a[data-cart-link]
 *                                 refreshed on the document 'cart:updated' event
 *                                 (detail.cart = /cart.js JSON, sent by product.js).
 *                                 The link label comes from data-label-one and
 *                                 data-label-other ("{count}" is replaced).
 *   [data-gift-card-qr]           QR code for gift_card.liquid; the attribute holds
 *                                 gift_card.qr_identifier, data-qr-alt the image
 *                                 alt text. Needs Shopify's vendor/qrcode.js first.
 * Public: window.Groply.{gallerySelect, initGalleries, initQty, submitForm, updateCartCount}
 *         (window.FurVolt, window.Hoomly, window.TurfCo and window.Nexus are
 *         the same object, kept as internal aliases for code written against
 *         the earlier builds of this kit; new code calls window.Groply).
 */
(function () {
  'use strict';

  var doc = document;
  var root = doc.documentElement;
  root.classList.remove('no-js');
  root.classList.add('js');

  function all(selector, context) {
    return Array.prototype.slice.call((context || doc).querySelectorAll(selector));
  }

  function directSummary(details) {
    for (var i = 0; i < details.children.length; i++) {
      if (details.children[i].tagName === 'SUMMARY') return details.children[i];
    }
    return null;
  }

  var reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var desktopQuery = window.matchMedia ? window.matchMedia('(min-width: 990px)') : null;

  /* ---------------------------------------------------------------------
   * 1. Drawers and dropdowns (details/summary)
   * ------------------------------------------------------------------- */
  // While a drawer is open it acts as a modal: everything outside it is made
  // inert (no focus, hidden from assistive tech) and Tab wraps inside it.
  var inertNodes = [];

  function releaseInert() {
    inertNodes.forEach(function (node) { node.inert = false; });
    inertNodes = [];
  }

  function makeOutsideInert(drawer) {
    releaseInert();
    var node = drawer;
    while (node && node !== doc.body && node.parentElement) {
      var parent = node.parentElement;
      for (var i = 0; i < parent.children.length; i++) {
        var sibling = parent.children[i];
        if (sibling === node || sibling.inert || /^(SCRIPT|STYLE|LINK|TEMPLATE)$/.test(sibling.tagName)) continue;
        sibling.inert = true;
        inertNodes.push(sibling);
      }
      node = parent;
    }
  }

  function openDrawer() {
    var open = all('details[data-drawer][open]');
    return open.length ? open[open.length - 1] : null;
  }

  function syncDrawerLock() {
    var drawer = openDrawer();
    root.classList.toggle('drawer-open', !!drawer);
    if (drawer) makeOutsideInert(drawer);
    else releaseInert();
  }

  function drawerFocusables(drawer) {
    return all('summary, a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', drawer)
      .filter(function (node) {
        // Rendered controls only (content of closed sub-menus has no boxes).
        // getClientRects, not offsetParent: the open toggle is position: fixed.
        return node.getClientRects().length > 0;
      });
  }

  doc.addEventListener('keydown', function (event) {
    if (event.key !== 'Tab') return;
    var drawer = openDrawer();
    if (!drawer) return;
    var focusables = drawerFocusables(drawer);
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    var active = doc.activeElement;
    if (event.shiftKey) {
      if (active === first || !drawer.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last || !drawer.contains(active)) {
      event.preventDefault();
      first.focus();
    }
  });

  function syncSummary(details) {
    var summary = directSummary(details);
    if (!summary) return;
    var isOpen = details.hasAttribute('open');
    summary.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    var openLabel = summary.getAttribute('data-label-open');
    var closeLabel = summary.getAttribute('data-label-closed');
    if (openLabel && closeLabel) summary.setAttribute('aria-label', isOpen ? closeLabel : openLabel);
  }

  function closeDetails(details, returnFocus) {
    if (!details || !details.hasAttribute('open')) return;
    details.removeAttribute('open');
    syncSummary(details);
    if (details.hasAttribute('data-drawer')) syncDrawerLock();
    if (returnFocus) {
      var summary = directSummary(details);
      if (summary) summary.focus();
    }
  }

  function initDisclosures(context) {
    all('details[data-drawer], details[data-dropdown]', context).forEach(syncSummary);
  }

  // toggle does not bubble: listen in the capture phase.
  doc.addEventListener('toggle', function (event) {
    var details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;
    var isDrawer = details.hasAttribute('data-drawer');
    var isDropdown = details.hasAttribute('data-dropdown');
    if (!isDrawer && !isDropdown) return;

    syncSummary(details);

    if (details.open && isDropdown) {
      all('details[data-dropdown][open]').forEach(function (other) {
        if (other !== details && !other.contains(details)) closeDetails(other, false);
      });
    }

    if (isDrawer) {
      syncDrawerLock();
      if (details.open) {
        var target = details.querySelector('[data-drawer-autofocus]');
        if (target) window.setTimeout(function () { target.focus(); }, 30);
      }
    }
  }, true);

  doc.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    var open = all('details[data-drawer][open], details[data-dropdown][open]');
    if (!open.length) return;
    // Close the innermost open disclosure first.
    var active = doc.activeElement;
    var target = null;
    for (var i = open.length - 1; i >= 0; i--) {
      if (active && open[i].contains(active)) { target = open[i]; break; }
    }
    closeDetails(target || open[open.length - 1], true);
  });

  doc.addEventListener('click', function (event) {
    var el = event.target instanceof Element ? event.target : null;
    if (!el) return;

    var closer = el.closest('[data-drawer-close]');
    if (closer) {
      var drawer = closer.closest('details[data-drawer]');
      if (drawer) {
        event.preventDefault();
        closeDetails(drawer, true);
        return;
      }
    }

    // Following an in-page link from inside a drawer closes it.
    var link = el.closest('a[href]');
    if (link) {
      var parentDrawer = link.closest('details[data-drawer][open]');
      if (parentDrawer && link.getAttribute('href').charAt(0) === '#') closeDetails(parentDrawer, false);
    }

    all('details[data-dropdown][open]').forEach(function (details) {
      if (!details.contains(el)) closeDetails(details, false);
    });
  });

  if (desktopQuery) {
    var onBreakpoint = function (query) {
      if (query.matches) all('details[data-drawer][open]').forEach(function (d) { closeDetails(d, false); });
    };
    if (desktopQuery.addEventListener) desktopQuery.addEventListener('change', onBreakpoint);
    else if (desktopQuery.addListener) desktopQuery.addListener(onBreakpoint);
  }

  /* ---------------------------------------------------------------------
   * 2. Header: scroll shadow class + --header-height
   * ------------------------------------------------------------------- */
  var headerObserver = null;

  function initHeader() {
    var header = doc.querySelector('[data-header]');
    if (!header) return;

    var ticking = false;
    var update = function () {
      header.classList.toggle('is-scrolled', (window.scrollY || window.pageYOffset) > 4);
      ticking = false;
    };
    if (!header.__nxScroll) {
      header.__nxScroll = true;
      window.addEventListener('scroll', function () {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(update);
        }
      }, { passive: true });
    }
    update();

    var setHeight = function () {
      root.style.setProperty('--header-height', header.offsetHeight + 'px');
    };
    setHeight();
    if ('ResizeObserver' in window) {
      if (headerObserver) headerObserver.disconnect();
      headerObserver = new ResizeObserver(setHeight);
      headerObserver.observe(header);
    } else {
      window.addEventListener('resize', setHeight);
    }
  }

  /* ---------------------------------------------------------------------
   * 3. Quantity steppers
   * ------------------------------------------------------------------- */
  function qtyInput(wrapper) {
    return wrapper ? wrapper.querySelector('input[type="number"]') : null;
  }

  function qtyBounds(input) {
    var min = input.min !== '' ? parseFloat(input.min) : 0;
    var max = input.max !== '' ? parseFloat(input.max) : Infinity;
    return { min: isNaN(min) ? 0 : min, max: isNaN(max) ? Infinity : max };
  }

  function syncQtyButtons(wrapper) {
    var input = qtyInput(wrapper);
    if (!input) return;
    var value = parseFloat(input.value);
    var bounds = qtyBounds(input);
    all('[data-qty-step]', wrapper).forEach(function (button) {
      var step = parseFloat(button.getAttribute('data-qty-step')) || 0;
      var blocked = input.disabled || isNaN(value) ? false : (step < 0 ? value <= bounds.min : value >= bounds.max);
      button.disabled = input.disabled || blocked;
    });
  }

  function initQty(context) {
    all('[data-qty]', context).forEach(syncQtyButtons);
  }

  doc.addEventListener('click', function (event) {
    var button = event.target instanceof Element ? event.target.closest('[data-qty-step]') : null;
    if (!button) return;
    var wrapper = button.closest('[data-qty]');
    var input = qtyInput(wrapper);
    if (!input || input.disabled) return;
    event.preventDefault();

    var direction = parseFloat(button.getAttribute('data-qty-step')) || 0;
    var step = parseFloat(input.step) || 1;
    var bounds = qtyBounds(input);
    var current = parseFloat(input.value);
    if (isNaN(current)) current = bounds.min;
    var next = Math.min(bounds.max, Math.max(bounds.min, current + direction * step));
    if (next === current) return;

    input.value = next;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    syncQtyButtons(wrapper);
  });

  doc.addEventListener('input', function (event) {
    var el = event.target instanceof Element ? event.target : null;
    if (el && el.matches('[data-qty] input[type="number"]')) syncQtyButtons(el.closest('[data-qty]'));
  });

  /* ---------------------------------------------------------------------
   * 4. Auto-submit forms
   * ------------------------------------------------------------------- */
  function submitForm(form) {
    if (!form) return;
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.submit();
  }

  var submitTimers = typeof WeakMap === 'function' ? new WeakMap() : null;

  // Keyboard users step through a closed <select> with the arrow keys, and
  // some browsers fire 'change' on every step. A select changed from the
  // keyboard is therefore submitted on Enter or when it loses focus, never
  // mid-choice (WCAG 3.2.2). Mouse and touch changes still submit at once.
  var keyboardSelect = null;

  doc.addEventListener('keydown', function (event) {
    var el = event.target;
    if (!el || el.tagName !== 'SELECT' || !el.closest('[data-autosubmit]')) return;
    if (event.key === 'Enter') {
      if (el.__nxPending) {
        event.preventDefault();
        el.__nxPending = false;
        submitForm(el.form || el.closest('form'));
      }
      return;
    }
    if (event.key !== 'Tab' && event.key !== 'Shift') keyboardSelect = el;
  }, true);

  doc.addEventListener('pointerdown', function () { keyboardSelect = null; }, true);

  doc.addEventListener('change', function (event) {
    var el = event.target instanceof Element ? event.target : null;
    if (!el || !el.closest('[data-autosubmit]')) return;
    var form = el.form || el.closest('form');
    if (!form) return;

    if (el.tagName === 'SELECT' && keyboardSelect === el) {
      if (!el.__nxPending) {
        el.__nxPending = true;
        el.addEventListener('blur', function onBlur() {
          el.removeEventListener('blur', onBlur);
          if (!el.__nxPending) return;
          el.__nxPending = false;
          submitForm(form);
        });
      }
      return;
    }

    var typed = el.matches('input[type="number"], input[type="text"], input[type="search"], textarea');
    var delay = typed ? 500 : 0;
    if (submitTimers) {
      window.clearTimeout(submitTimers.get(form));
      submitTimers.set(form, window.setTimeout(function () { submitForm(form); }, delay));
    } else {
      window.setTimeout(function () { submitForm(form); }, delay);
    }
  });

  /* ---------------------------------------------------------------------
   * 5. Media gallery
   * ------------------------------------------------------------------- */
  function gallerySelect(gallery, mediaId, options) {
    if (typeof gallery === 'string') gallery = doc.querySelector(gallery);
    if (!gallery || mediaId == null) return false;
    var id = String(mediaId);
    var items = all('[data-gallery-item]', gallery);
    var item = null;
    for (var i = 0; i < items.length; i++) {
      if (items[i].getAttribute('data-media-id') === id) { item = items[i]; break; }
    }
    if (!item) return false;

    items.forEach(function (node) { node.classList.toggle('is-active', node === item); });
    all('[data-gallery-thumb]', gallery).forEach(function (thumb) {
      var on = thumb.getAttribute('data-gallery-thumb') === id;
      thumb.classList.toggle('is-active', on);
      if (on) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });

    var fromScroll = options && options.fromScroll;
    var track = gallery.querySelector('[data-gallery-track]');
    if (track && !fromScroll && track.scrollWidth > track.clientWidth + 1) {
      var left = item.offsetLeft - (parseFloat(window.getComputedStyle(track).paddingLeft) || 0);
      if (typeof track.scrollTo === 'function') {
        track.scrollTo({ left: left, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      } else {
        track.scrollLeft = left;
      }
    }

    if (gallery.__nxCurrent !== id) {
      gallery.__nxCurrent = id;
      gallery.dispatchEvent(new CustomEvent('gallery:change', { bubbles: true, detail: { mediaId: id } }));
    }
    return true;
  }

  function initGallery(gallery) {
    if (gallery.__nxGallery) return;
    gallery.__nxGallery = true;

    gallery.addEventListener('click', function (event) {
      var thumb = event.target instanceof Element ? event.target.closest('[data-gallery-thumb]') : null;
      if (!thumb || !gallery.contains(thumb)) return;
      event.preventDefault();
      gallerySelect(gallery, thumb.getAttribute('data-gallery-thumb'));
    });

    gallery.addEventListener('gallery:select', function (event) {
      if (event.detail && event.detail.mediaId != null) gallerySelect(gallery, event.detail.mediaId);
    });

    var items = all('[data-gallery-item]', gallery);
    var start = gallery.querySelector('[data-gallery-item].is-active') || items[0];
    if (start) {
      start.classList.add('is-active');
      gallery.__nxCurrent = start.getAttribute('data-media-id');
      all('[data-gallery-thumb]', gallery).forEach(function (thumb) {
        var on = thumb.getAttribute('data-gallery-thumb') === gallery.__nxCurrent;
        thumb.classList.toggle('is-active', on);
        if (on) thumb.setAttribute('aria-current', 'true');
      });
    }

    // Keep thumbs in sync when the shopper swipes a scroll-snap track.
    var track = gallery.querySelector('[data-gallery-track]');
    if (track && 'IntersectionObserver' in window && gallery.getAttribute('data-gallery') !== 'swap') {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            gallerySelect(gallery, entry.target.getAttribute('data-media-id'), { fromScroll: true });
          }
        });
      }, { root: track, threshold: [0.6] });
      items.forEach(function (node) { observer.observe(node); });
    }
  }

  function initGalleries(context) {
    all('[data-gallery]', context).forEach(initGallery);
  }

  /* ---------------------------------------------------------------------
   * 6. Rich-text tables: horizontal scroll wrapper
   * ------------------------------------------------------------------- */
  function wrapTables(context) {
    all('.rte table, .shopify-policy__body table', context).forEach(function (table) {
      var parent = table.parentElement;
      if (!parent || parent.classList.contains('table-wrap')) return;
      var wrap = doc.createElement('div');
      wrap.className = 'table-wrap';
      parent.insertBefore(wrap, table);
      wrap.appendChild(table);
      if (wrap.scrollWidth > wrap.clientWidth + 1) wrap.setAttribute('tabindex', '0');
    });
  }

  /* ---------------------------------------------------------------------
   * 7. Cart count + cart link label after an AJAX change
   * ------------------------------------------------------------------- */
  function updateCartCount(count) {
    count = parseInt(count, 10);
    if (isNaN(count) || count < 0) return;
    all('[data-cart-count]').forEach(function (node) {
      node.textContent = String(count);
      node.hidden = count === 0;
    });
    all('[data-cart-link]').forEach(function (link) {
      var one = link.getAttribute('data-label-one');
      var other = link.getAttribute('data-label-other');
      var label = count === 1 && one ? one : (other ? other.split('{count}').join(String(count)) : null);
      if (label) link.setAttribute('aria-label', label);
    });
  }

  doc.addEventListener('cart:updated', function (event) {
    var cart = event.detail && event.detail.cart;
    if (cart && typeof cart.item_count === 'number') updateCartCount(cart.item_count);
  });

  /* ---------------------------------------------------------------------
   * 8. Gift card QR code (gift_card.liquid loads vendor/qrcode.js first)
   * ------------------------------------------------------------------- */
  function initGiftCardQr(context) {
    if (typeof window.QRCode !== 'function') return;
    all('[data-gift-card-qr]', context).forEach(function (node) {
      var text = node.getAttribute('data-gift-card-qr');
      if (!text || node.__nxQr) return;
      node.__nxQr = true;
      try {
        new window.QRCode(node, {
          text: text,
          width: 120,
          height: 120,
          imageAltText: node.getAttribute('data-qr-alt') || ''
        });
        node.removeAttribute('title');
        node.hidden = false;
      } catch (error) {
        /* The code is printed as text right above, so nothing is lost. */
      }
    });
  }

  /* ---------------------------------------------------------------------
   * Init + theme editor support
   * ------------------------------------------------------------------- */
  function init(context) {
    initDisclosures(context);
    initQty(context);
    initGalleries(context);
    wrapTables(context);
    initGiftCardQr(context);
  }

  function boot() {
    initHeader();
    init(doc);
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
  else boot();

  doc.addEventListener('shopify:section:load', function (event) {
    init(event.target);
    if (event.target && event.target.querySelector && event.target.querySelector('[data-header]')) initHeader();
  });

  var api = window.Groply || window.FurVolt || window.Hoomly || window.TurfCo || window.Nexus || {};
  api.gallerySelect = gallerySelect;
  api.initGalleries = initGalleries;
  api.initQty = initQty;
  api.submitForm = submitForm;
  api.updateCartCount = updateCartCount;
  window.Groply = api;
  window.FurVolt = api;
  window.Hoomly = api;
  window.TurfCo = api;
  window.Nexus = api;
})();
